import { userModule } from '@/module/userModule';
import ApiService, { type RequestOptions } from '@/services/apiService';
import { handleResult } from '@/helpers/utils/functionality-utils';
import { awsApiErrorCodes } from '@/constants/auth-constants';
import type { StoreBaseResponse } from '@/types/types';
import type { NullableAddUserPayload } from '@/types/userlist';
import { signUp } from '@aws-amplify/auth';
import type { NullableUpdateSelfRequestType } from '@/types/authresponse';

export class UserService {
  private userApiService: ApiService;
  constructor() {
    this.userApiService = new ApiService(userModule);
  }

  async getUserData(): Promise<StoreBaseResponse> {
    try {
      const response = await this.userApiService.get('/api/v1/auth/info');
      return handleResult(response);
    } catch (error) {
      console.error(error);
      return { error: true, message: JSON.stringify(error) };
    }
  }

  async getUserDataForEdit(
    param: RequestOptions['params'],
  ): Promise<StoreBaseResponse> {
    if (param?.id === undefined)
      return { error: true, message: 'No user id provided' };
    const response = await this.userApiService.get(
      `/api/v1/auth/user/${param.id}/bio`,
    );

    return handleResult(response);
  }

  //userlisting service
  async getUserList(
    params: RequestOptions['params'],
  ): Promise<StoreBaseResponse> {
    // for table with pagination
    try {
      const response = await this.userApiService.get(
        '/api/v1/auth/user/list',
        params,
      );

      return handleResult(response);
    } catch (error) {
      console.error(error);
      return { error: true, message: JSON.stringify(error) };
    }
  }

  //userlogin service
  async userLogin(
    email_id: string,
    password: string,
    tenant_code: string,
  ): Promise<StoreBaseResponse> {
    const response = await this.userApiService.post('/api/v1/auth/login', {
      email_id,
      password,
      tenant_code,
    });
    return handleResult(response);
  }

  //addUser or modify if id is present
  async addUser(body: NullableAddUserPayload, id: number | undefined) {
    const response = await this.userApiService.post<null>(
      `/api/v1/auth/user${id ? `/${id}/modify` : '/add'}`,
      body,
    );
    return handleResult(response);
  }

  // roles
  async userRoles(): Promise<StoreBaseResponse> {
    const response = await this.userApiService.get('/api/v1/master/roles');
    return handleResult(response);
  }

  async getDepartments(): Promise<StoreBaseResponse> {
    const response = await this.userApiService.get(
      '/api/v1/master/departments',
    );
    return handleResult(response);
  }

  async addUserToAWS(body: { email: string; password: string }) {
    try {
      const response = await signUp({
        username: body.email,
        password: body.password,
      });
      return { error: false, data: response };
    } catch (error) {
      if (error instanceof Error) {
        return {
          error: true,
          message: error.name
            ? awsApiErrorCodes[error.name as keyof typeof awsApiErrorCodes]
            : JSON.stringify(error),
        };
      } else {
        return {
          error: true,
          message: JSON.stringify(error),
        };
      }
    }
  }

  async getAllUsers(): Promise<StoreBaseResponse> {
    const response = await this.userApiService.get('/api/v1/master/users');
    return handleResult(response);
  }

  async uploadProfile(
    formData: FormData,
    id?: number,
  ): Promise<StoreBaseResponse> {
    const response = await this.userApiService.post(
      `/api/v1/auth/user/${id}/pic/upload`,
      formData,
    );
    return handleResult(response);
  }

  async uploadProfilePicForSelf(formData: FormData) {
    const response = await this.userApiService.post<string>(
      `/api/v1/auth/profile/pic`,
      formData,
    );
    return handleResult(response);
  }

  async updateProfileInfoForSelf(data: NullableUpdateSelfRequestType) {
    const response = await this.userApiService.patch<null>(
      `/api/v1/auth/profile/update`,
      data,
    );
    return handleResult(response);
  }
}
